import {Suspense, useEffect, useMemo, useRef} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {debounce} from '@github/mini-throttle'
import {announce} from '@github-ui/aria-live'
import {isLoggedIn} from '@github-ui/client-env'
import {ThreadPreviewSkeleton} from './ThreadPreviewSkeleton'
import {useInfiniteIssueCommentsPageData} from '../../page-data/loaders/use-issue-comments'
import {useInfiniteReviewSummariesPageData} from '../../page-data/loaders/use-review-summaries'
import {IssueCommentItem} from './IssueCommentItem'
import {ReviewSummaryItem} from './ReviewSummaryItem'
import {CommentsErrorFallback} from './CommentsErrorFallback'
import {CommentsZeroState} from './CommentsZeroState'
import type {ConversationItem} from './utils/build-conversation-items'
import {conversationItemToIssueComment} from './utils/conversation-item-to-issue-comment'
import {ThreadPreview} from './ThreadPreview'
import {HydrateThreadPositioning} from './HydrateThreadPositioning'

export interface IssueCommentsContentProps {
  filteredThreadIds: Set<string>
  pathName: string
  pullRequestAuthorLogin: string
  conversationItems: ConversationItem[]
  onQuoteReply: (quotedText: string) => void
  onNavigateToDiffComment?: () => void
}

/**
 * Data loading container that fetches issue comments and review summaries.
 * Automatically fetches all available pages on mount when the server returns
 * a Link header with rel="next", up to the configured pagination limit.
 */
export function IssueCommentsContent({
  filteredThreadIds,
  pathName,
  pullRequestAuthorLogin,
  conversationItems,
  onQuoteReply,
  onNavigateToDiffComment,
}: IssueCommentsContentProps) {
  const {
    hasNextPage,
    isFetchingNextPage: isFetchingIssueComments,
    fetchNextPage,
  } = useInfiniteIssueCommentsPageData({
    pathName,
  })

  // Fetch review summaries in parallel with issue comments
  const {
    hasNextPage: hasNextReviewSummariesPage,
    isFetchingNextPage: isFetchingReviewSummaries,
    fetchNextPage: fetchNextReviewSummariesPage,
  } = useInfiniteReviewSummariesPageData({
    pathName,
  })

  // Track if we've initiated a fetch to prevent duplicate requests
  const fetchingRef = useRef(false)
  const reviewSummariesFetchingRef = useRef(false)

  // Automatically fetch next page when available and not already fetching
  useEffect(() => {
    let cancelled = false
    const fetchAllPages = async () => {
      if (hasNextPage && !isFetchingIssueComments && !fetchingRef.current) {
        fetchingRef.current = true
        await fetchNextPage()
        if (!cancelled) fetchingRef.current = false
      }
    }
    void fetchAllPages()
    return () => {
      cancelled = true
    }
  }, [hasNextPage, isFetchingIssueComments, fetchNextPage])

  // Automatically fetch next review summaries page when available
  useEffect(() => {
    let cancelled = false
    const fetchAllReviewSummariesPages = async () => {
      if (hasNextReviewSummariesPage && !isFetchingReviewSummaries && !reviewSummariesFetchingRef.current) {
        reviewSummariesFetchingRef.current = true
        await fetchNextReviewSummariesPage()
        if (!cancelled) reviewSummariesFetchingRef.current = false
      }
    }
    void fetchAllReviewSummariesPages()
    return () => {
      cancelled = true
    }
  }, [hasNextReviewSummariesPage, isFetchingReviewSummaries, fetchNextReviewSummariesPage])

  // Show loading state when either source is actively fetching or has more pages
  const isLoadingMore =
    isFetchingIssueComments || hasNextPage || isFetchingReviewSummaries || hasNextReviewSummariesPage

  return (
    <IssueCommentsComponent
      conversationItems={conversationItems}
      filteredThreadIds={filteredThreadIds}
      isLoadingMore={isLoadingMore}
      pullRequestAuthorLogin={pullRequestAuthorLogin}
      onQuoteReply={onQuoteReply}
      onNavigateToDiffComment={onNavigateToDiffComment}
    />
  )
}

interface IssueCommentsComponentProps {
  conversationItems: ConversationItem[]
  filteredThreadIds: Set<string>
  isLoadingMore: boolean
  pullRequestAuthorLogin: string
  onQuoteReply: (quotedText: string) => void
  onNavigateToDiffComment?: () => void
}

/**
 * Presentational component that renders issue comments and review summaries.
 * Handles the display logic for comments, loading states, and zero states.
 */
function IssueCommentsComponent({
  conversationItems,
  filteredThreadIds,
  isLoadingMore,
  pullRequestAuthorLogin,
  onQuoteReply,
  onNavigateToDiffComment,
}: IssueCommentsComponentProps) {
  const canReact = isLoggedIn()
  const announceRef = useRef<HTMLDivElement>(null)
  const debouncedAnnounce = useMemo(() => debounce(announce, 300), [])

  const hasConversationItems = conversationItems.length > 0

  const visibleComments = useMemo(
    () => conversationItems.filter(item => filteredThreadIds.has(conversationItemToIssueComment(item)?.id ?? '')),
    [conversationItems, filteredThreadIds],
  )

  useEffect(() => {
    // Only announce when we've finished loading all comments
    if (isLoadingMore) return

    const announceText =
      visibleComments.length > 0
        ? `${visibleComments.length} ${visibleComments.length === 1 ? 'comment' : 'comments'}`
        : `No comments found`

    debouncedAnnounce(announceText, {element: announceRef.current as HTMLElement})
  }, [isLoadingMore, debouncedAnnounce, visibleComments])

  const unassignedThreads = useMemo(
    () => visibleComments.filter(item => item.type === 'thread').map(item => item.data),
    [visibleComments],
  )

  return (
    <>
      {visibleComments.length > 0 ? (
        <ErrorBoundary boundaryName="IssueCommentsContent" fallback={<CommentsErrorFallback />}>
          <Suspense fallback={<ThreadPreviewSkeleton />}>
            <HydrateThreadPositioning threads={unassignedThreads} />
            <div className="d-flex flex-column position-relative width-full gap-3">
              {visibleComments.map(item =>
                // TODO: limit/page the rendered items so that we don't try to hydrate thread positioning for every
                // thread at once
                item.type === 'review-summary' ? (
                  <ReviewSummaryItem
                    key={item.key}
                    reviewSummary={item.data.summary}
                    reviewThreads={item.data.threads}
                    pullRequestAuthorLogin={pullRequestAuthorLogin}
                    canReact={canReact}
                    onQuoteReply={onQuoteReply}
                    withReviewStateIcon
                    onNavigateToDiffComment={onNavigateToDiffComment}
                  />
                ) : item.type === 'thread' ? (
                  <ThreadPreview
                    key={item.key}
                    thread={item.data}
                    selectedReviewId={null}
                    onNavigateToDiffComment={onNavigateToDiffComment}
                    headingLevel="h2"
                  />
                ) : (
                  <IssueCommentItem
                    key={item.key}
                    comment={item.data}
                    pullRequestAuthorLogin={pullRequestAuthorLogin}
                    canReact={canReact}
                    onQuoteReply={onQuoteReply}
                  />
                ),
              )}
              {isLoadingMore && <ThreadPreviewSkeleton count={1} />}
            </div>
          </Suspense>
        </ErrorBoundary>
      ) : isLoadingMore ? (
        <ThreadPreviewSkeleton />
      ) : (
        <CommentsZeroState hasItems={hasConversationItems} />
      )}
      <div className="sr-only" aria-live="polite" aria-atomic="true" ref={announceRef} />
    </>
  )
}
